import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { AuthService } from "./services/auth.service";
import { GlobalService } from "./shared/global.service";
import { CookieService } from "./services/cookie.service";
import { CompilationSyncService } from "./services/compilation-sync.service";


@Component({
	selector: "app-root",
	templateUrl: "./app.component.html",
	styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit, OnDestroy {
	title = "amaojor-survey";

	// ######################################
	// FUNCTION: constructor
	constructor(
		public g: GlobalService,
		public auth: AuthService,
		public translate: TranslateService,
		private router: Router,
		private cookieService: CookieService,
		private ngZone: NgZone,
		private cdr: ChangeDetectorRef,
		// Iniettato solo per istanziare il servizio all'avvio: i suoi listener di
		// risveglio tab (visibilitychange/pageshow → enableNetwork) devono coprire
		// tutta l'app (area cliente/admin), non solo la compilazione.
		private compilationSync: CompilationSyncService
	) {
		translate.addLangs(["en", "it"]);
		translate.setDefaultLang("it");
		translate.use("it");
	}

	// ######################################
	// FUNCTION: useLanguage
	useLanguage(language: string) {
		this.translate.use(language);
		this.g.selectedLanguage = language;
	}

	openCookiePreferences() {
		this.cookieService.openPreferences();
	}

	// ######################################
	// FUNCTION: goTo
	goTo(location: string) {
		this.router.navigateByUrl(location);
	}

	showFooter = true;
	private _accumDown = 0;
	private _accumUp = 0;
	private _lastYByEl = new WeakMap<EventTarget, number>();

	/** Listener in capture su document: gli eventi scroll non fanno bubbling ma
	 *  attraversano la capture phase, quindi un solo listener copre window/body
	 *  e qualsiasi container interno — zero lavoro quando la pagina è ferma
	 *  (prima: rAF loop con querySelectorAll("*") ad ogni frame). */
	private onAnyScroll = (ev: Event): void => {
		const t: any = ev.target;
		const y =
			t === document || t === window || t === document.scrollingElement
				? Math.max(
						window.scrollY || 0,
						window.pageYOffset || 0,
						document.documentElement?.scrollTop || 0,
						document.body?.scrollTop || 0
				  )
				: t.scrollTop || 0;
		const last = this._lastYByEl.has(t) ? this._lastYByEl.get(t) : y;
		this._lastYByEl.set(t, y);
		const delta = y - last;

		if (delta > 0) {
			this._accumDown += delta;
			this._accumUp = 0;
			if (this._accumDown >= 20 && this.showFooter) {
				this.ngZone.run(() => {
					this.showFooter = false;
					this.cdr.detectChanges();
				});
				this._accumDown = 0;
			}
		} else if (delta < 0) {
			this._accumUp += -delta;
			this._accumDown = 0;
			if (this._accumUp >= 30 && !this.showFooter) {
				this.ngZone.run(() => {
					this.showFooter = true;
					this.cdr.detectChanges();
				});
				this._accumUp = 0;
			}
		}
	};

	ngOnInit(): void {
		// Fuori dalla zone: aggiorna showFooter solo quando cambia davvero.
		this.ngZone.runOutsideAngular(() => {
			document.addEventListener("scroll", this.onAnyScroll, { capture: true, passive: true });
		});

		const splash = document.getElementById("app-splash");
		if (splash) {
			requestAnimationFrame(() => {
				splash.classList.add("is-hidden");
				setTimeout(() => splash.remove(), 350);
			});
		}
	}

	ngOnDestroy(): void {
		document.removeEventListener("scroll", this.onAnyScroll, true);
	}
}
